import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { LockFill, PersonFill, EnvelopeFill } from 'react-bootstrap-icons';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import loginImg from 'src/assets/images/login.png';
import { useAppDispatch } from 'src/hooks';
import { signUp, getTiers } from 'src/reducers/user';
import { Button, Input, Text } from 'src/shared';
import { Size } from 'src/shared/text';
import { getAuthToken, setAuthToken } from 'src/utils/auth';
import { Colors } from 'src/utils/color';
import { safeLog, safeErrorLog } from 'src/utils/safeLogging';
import TermsAndConditionsModal from 'src/components/TermsAndConditionsModal';
import MFASetupModal from 'src/components/MFASetupModal';
import { getTermsAgreementStatus, setTermsAgreementStatus, hasUserAgreedToTerms } from 'src/utils/termsAgreement';
import { useTermsAgreement } from 'src/hooks/useTermsAgreement';

import {
    Bottomcontent,
    Bottomterms,
    Cardbodyimg,
    Cardform,
    ForgotPassword,
    Wrapper
} from '../login/styles';

interface Tier {
    id: number;
    name: string;
    description: string;
    max_users: number;
    max_storage_gb: number;
    max_api_calls_per_month: number;
    price_per_month: number;
    is_active: boolean;
}

const SignUp = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [tiers, setTiers] = useState<Tier[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showMFASetup, setShowMFASetup] = useState(false);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(() => hasUserAgreedToTerms());
    const { hasAgreed: globalTermsAgreed, refreshAgreementStatus } = useTermsAgreement();

    const validationSchema = yup.object().shape({
        first_name: yup.string().required('First name is required'),
        last_name: yup.string().required('Last name is required'),
        email: yup
            .string()
            .required('Email is required')
            .email('Invalid email'),
        password: yup
            .string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters'),
        confirm_password: yup
            .string()
            .required('Please confirm your password')
            .oneOf([yup.ref('password')], 'Passwords must match'),
        tier_id: yup.number().required('Please select a subscription tier')
    });

    // Debug: Track errorMessage state changes
    useEffect(() => {
        console.log('🔍 errorMessage state changed to:', errorMessage);
    }, [errorMessage]);

    useEffect(() => {
        // Load available tiers
        dispatch(getTiers({}))
            .unwrap()
            .then((response: any) => {
                if (response?.success) {
                    setTiers(response.data.tiers);
                }
            })
            .catch((err: any) => {
                console.error('Failed to load tiers:', err);
            });
    }, [dispatch]);

    // Refresh terms agreement status when component mounts
    useEffect(() => {
        console.log('🏠 Signup page component mounted');
        refreshAgreementStatus();
        // Add a small delay to ensure session storage is updated
        const timer = setTimeout(() => {
            // Debug: Check terms agreement status on mount
            const termsStatusOnMount = getTermsAgreementStatus();
            const sessionStorageAgreed = hasUserAgreedToTerms();
            console.log('🔍 Signup page mounted - Terms agreement status:', {
                hasAgreedToTerms,
                globalTermsAgreed,
                sessionStorageAgreed,
                sessionStorageStatus: termsStatusOnMount
            });
        }, 100);
        
        return () => clearTimeout(timer);
    }, [refreshAgreementStatus, hasAgreedToTerms, globalTermsAgreed]);

    const onSubmit = async (data: any) => {
        // Debug: Check terms agreement status on form submission
        const sessionStorageAgreed = hasUserAgreedToTerms();
        const termsStatusForSubmission = getTermsAgreementStatus();
        console.log('🔍 Form submitted - Terms agreement status:', {
            hasAgreedToTerms,
            globalTermsAgreed,
            sessionStorageAgreed,
            sessionStorageStatus: termsStatusForSubmission
        });
        
        // Check both hook state and session storage directly
        const shouldProceed = globalTermsAgreed || sessionStorageAgreed;
        console.log('🎯 Should proceed with signup:', shouldProceed, {
            globalTermsAgreed,
            sessionStorageAgreed
        });
        
        if (!shouldProceed) {
            console.log('⚠️ Terms not agreed, showing modal');
            setShowTermsModal(true);
            return;
        }

        setLoading(true);
        setErrorMessage('');
        
        // Get terms consent data
        const termsStatus = getTermsAgreementStatus();
        
        // Validate terms consent data
        if (!termsStatus.timestamp || termsStatus.timestamp <= 0) {
            console.error('❌ Invalid consent timestamp:', termsStatus.timestamp);
            setErrorMessage('Terms consent data is invalid. Please agree to terms and conditions again.');
            setLoading(false);
            return;
        }
        
        // Debug logging for terms consent data
        console.log('🔍 Terms consent data being sent:', {
            terms_version: termsStatus.version,
            consent_timestamp: termsStatus.timestamp,
            browser_user_agent: termsStatus.browserUserAgent,
            browser_language: termsStatus.browserLanguage,
            browser_platform: termsStatus.browserPlatform,
            consent_data: termsStatus.consentData
        });
        
        // Safe logging - mask password
        safeLog("🟡 Sign-up form submitted!", data);
        
        dispatch(
            signUp({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                password: data.password,
                tier_id: data.tier_id,
                terms_consent: {
                    terms_version: termsStatus.version,
                    consent_timestamp: termsStatus.timestamp,
                    browser_user_agent: termsStatus.browserUserAgent,
                    browser_language: termsStatus.browserLanguage,
                    browser_platform: termsStatus.browserPlatform,
                    consent_data: termsStatus.consentData
                }
            })
        )
            .unwrap()
            .then((response: any) => {
                console.log('🔍 Signup response received:', response);
                if (response?.success) {
                    setAuthToken(response?.data?.auth_token);
                    
                    // Check if MFA setup is required
                    if (response?.data?.mfa_setup_required || response?.data?.mfa_required) {
                        // Show MFA setup modal instead of navigating
                        setShowMFASetup(true);
                    } else {
                        navigate('/upload');
                    }
                } else {
                    console.log('❌ Signup failed - response details:', {
                        success: response?.success,
                        message: response?.message,
                        code: response?.code,
                        status: response?.status
                    });
                    // Handle specific error codes
                    if (response?.code === 'email_already_exists') {
                        setErrorMessage('An account with this email address already exists. Please use a different email or try logging in.');
                    } else if (response?.code === 'terms_consent_required') {
                        setErrorMessage('You must agree to the terms and conditions to create an account.');
                    } else {
                        setErrorMessage(response?.message || 'Sign-up failed. Please try again.');
                    }
                }
            })
            .catch((err: any) => {
                console.log('❌ Signup error caught:', err);
                console.log('❌ Error object structure:', {
                    status: err?.status,
                    data: err?.data,
                    message: err?.message,
                    error: err?.error,
                    description: err?.description
                });
                safeErrorLog('Sign-up failed', err);
                
                // Handle network or server errors
                if (err?.status === 409) {
                    console.log('🎯 Setting 409 error message');
                    const message = 'An account with this email address already exists. Please use a different email or try logging in.';
                    console.log('📝 Setting error message to:', message);
                    setErrorMessage(message);
                    console.log('✅ Error message set');
                } else if (err?.status === 400) {
                    console.log('🎯 Setting 400 error message');
                    const message = err?.data?.message || 'Invalid signup data. Please check your information and try again.';
                    console.log('📝 Setting error message to:', message);
                    setErrorMessage(message);
                    console.log('✅ Error message set');
                } else {
                    console.log('🎯 Setting generic error message');
                    const message = 'Something went wrong. Please try again.';
                    console.log('📝 Setting error message to:', message);
                    setErrorMessage(message);
                    console.log('✅ Error message set');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleTermsClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowTermsModal(true);
    };

    const handleAgreeToTerms = () => {
        setHasAgreedToTerms(true);
        setTermsAgreementStatus(true);
        refreshAgreementStatus(); // Refresh the global hook state
    };

    // Re-route if already logged in
    if (getAuthToken()) return <Navigate to="/upload" />;

    return (
        <div className="main-row">
            <Wrapper>
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <Text
                                    padding={8}
                                    fontSize={Size.Medium}
                                    fontWeight="bold"
                                    color="white"
                                >
                                    Create Account
                                </Text>
                            </div>
                            <Cardform>
                                <Cardbodyimg>
                                    <img src={loginImg} alt="signup-logo" />
                                </Cardbodyimg>
                                <div className="row">
                                    <Formik
                                        initialValues={{
                                            first_name: '',
                                            last_name: '',
                                            email: '',
                                            password: '',
                                            confirm_password: '',
                                            tier_id: 1
                                        }}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}
                                        validateOnMount={false}
                                    >
                                        {({
                                            values,
                                            handleChange,
                                            handleSubmit,
                                            errors,
                                            touched
                                        }) => (
                                            <Form onSubmit={handleSubmit}>
                                                {errorMessage && (
                                                    <Alert variant="danger">
                                                        {errorMessage}
                                                    </Alert>
                                                )}
                                                
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <Input
                                                            label="First Name"
                                                            icon={PersonFill}
                                                            onChange={handleChange('first_name')}
                                                            type="text"
                                                            name="first_name"
                                                            value={values.first_name}
                                                        />
                                                        {touched.first_name && errors.first_name && (
                                                            <Text
                                                                marginLeft={50}
                                                                marginBottom={0}
                                                                color={Colors.extra.red}
                                                            >
                                                                {errors.first_name}
                                                            </Text>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <Input
                                                            label="Last Name"
                                                            icon={PersonFill}
                                                            onChange={handleChange('last_name')}
                                                            type="text"
                                                            name="last_name"
                                                            value={values.last_name}
                                                        />
                                                        {touched.last_name && errors.last_name && (
                                                            <Text
                                                                marginLeft={50}
                                                                marginBottom={0}
                                                                color={Colors.extra.red}
                                                            >
                                                                {errors.last_name}
                                                            </Text>
                                                        )}
                                                    </div>
                                                </div>

                                                <Input
                                                    label="Email"
                                                    icon={EnvelopeFill}
                                                    onChange={handleChange('email')}
                                                    type="email"
                                                    name="email"
                                                    value={values.email}
                                                />
                                                {touched.email && errors.email && (
                                                    <Text
                                                        marginLeft={50}
                                                        marginBottom={0}
                                                        color={Colors.extra.red}
                                                    >
                                                        {errors.email}
                                                    </Text>
                                                )}

                                                <Input
                                                    label="Password"
                                                    icon={LockFill}
                                                    onChange={handleChange('password')}
                                                    type="password"
                                                    value={values.password}
                                                />
                                                {touched.password && errors.password && (
                                                    <Text
                                                        marginLeft={50}
                                                        marginBottom={0}
                                                        color={Colors.extra.red}
                                                    >
                                                        {errors.password}
                                                    </Text>
                                                )}

                                                <Input
                                                    label="Confirm Password"
                                                    icon={LockFill}
                                                    onChange={handleChange('confirm_password')}
                                                    type="password"
                                                    value={values.confirm_password}
                                                />
                                                {touched.confirm_password && errors.confirm_password && (
                                                    <Text
                                                        marginLeft={50}
                                                        marginBottom={0}
                                                        color={Colors.extra.red}
                                                    >
                                                        {errors.confirm_password}
                                                    </Text>
                                                )}

                                                <div className="form-group">
                                                    <label>Subscription Tier</label>
                                                    <select
                                                        className="form-control"
                                                        name="tier_id"
                                                        value={values.tier_id}
                                                        onChange={handleChange('tier_id')}
                                                    >
                                                        {tiers.map((tier) => (
                                                            <option key={tier.id} value={tier.id}>
                                                                {tier.name} - ${tier.price_per_month}/month
                                                                {tier.price_per_month === 0 && ' (Free)'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {tiers.length > 0 && (
                                                        <small className="form-text text-muted">
                                                            {tiers.find(t => t.id === values.tier_id)?.description}
                                                        </small>
                                                    )}
                                                </div>

                                                <Bottomcontent>
                                                    <Bottomterms>
                                                        <Text>
                                                            By creating an account, you agree to our{' '}
                                                            <button
                                                                type="button"
                                                                className="tnc-link"
                                                                onClick={handleTermsClick}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: globalTermsAgreed ? '#10b981' : '#3b82f6',
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                    padding: 0,
                                                                    font: 'inherit'
                                                                }}
                                                            >
                                                                terms and conditions.
                                                                {globalTermsAgreed && ' ✓'}
                                                            </button>
                                                        </Text>
                                                    </Bottomterms>
                                                    <div className="btn-sec">
                                                        <Button disabled={loading || !globalTermsAgreed}>
                                                            {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
                                                        </Button>
                                                    </div>
                                                </Bottomcontent>

                                                {!globalTermsAgreed && (
                                                    <div style={{ 
                                                        textAlign: 'center', 
                                                        marginTop: '15px',
                                                        padding: '10px',
                                                        backgroundColor: '#fef2f2',
                                                        border: '1px solid #fecaca',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <Text
                                                            fontSize={Size.X4Small}
                                                            color="#ef4444"
                                                            marginTop={0}
                                                            marginBottom={0}
                                                            fontFamily="Inter"
                                                        >
                                                            You must agree to terms and conditions to create an account
                                                        </Text>
                                                    </div>
                                                )}
                                                
                                                <ForgotPassword>
                                                    <Text>
                                                        Already have an account?{' '}
                                                        <Link to="/login">
                                                            Sign in here
                                                        </Link>
                                                    </Text>
                                                </ForgotPassword>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </Cardform>
                        </div>
                    </div>
                </div>
            </Wrapper>

            {/* Terms and Conditions Modal */}
            <TermsAndConditionsModal
                show={showTermsModal}
                onHide={() => setShowTermsModal(false)}
                onAgree={handleAgreeToTerms}
            />

            {/* MFA Setup Modal - Required after signup */}
            <MFASetupModal
                show={showMFASetup}
                onHide={() => {
                    setShowMFASetup(false);
                    // After MFA setup, navigate to upload page
                    navigate('/upload');
                }}
                onComplete={() => {
                    setShowMFASetup(false);
                    // After MFA setup is complete, navigate to upload page
                    navigate('/upload');
                }}
            />
        </div>
    );
};

export default SignUp;
